import type { HttpErrorResponse } from '@angular/common/http';

import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DndDropEvent, EffectAllowed } from 'ngx-drag-drop';
import { Subscription } from 'rxjs';
import { AlertService } from 'src/app/shared/services/alert.service';
import { DEFAULT_MODAL_OPTIONS } from 'src/app/shared/variables/variables';

import {
  AddQuickFiltersModalComponent,
  AddQuickFiltersModalResult,
} from '../add-quick-filters-modal/add-quick-filters-modal.component';
import { QuickFilter } from '../filter.interface';
import { FiltersDataService } from './../../../services/filters-data.service';

@Component({
  selector: 'app-manage-filters-modal',
  templateUrl: './manage-filters-modal.component.html',
})
export class ManageFiltersModalComponent<T> implements OnInit, OnDestroy {
  quickFilters: QuickFilter[] = [];
  filtersName: string;
  currentFiltersFormValues: T;
  isAddQuickFilterEnabled = false;

  draggable: DraggableOptions;
  quickFilterItems: QuickFilter[] = [];

  formFiltersEditor: UntypedFormGroup;
  formArrayItems: UntypedFormArray;

  newFilterName?: string;
  validationMessage: string;

  notDefaultDNDPlaceholderIsHidden = false;

  private notUniqueControlsValues: string[] = [];
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private filterService: FiltersDataService,
    private fb: UntypedFormBuilder,
    private alertService: AlertService,
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit() {
    this.quickFilterItems = this.quickFilters.map((x) => {
      return { ...x };
    });

    this.initDraggable();
    this.handleQuickFiltersCountsChanges();

    this.formFiltersEditor = this.fb.group({
      items: this.fb.array(
        this.quickFilterItems.map((x) => {
          return this.fb.control(x.name, [
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(40),
          ]);
        }),
        [this.uniqueNamesValidator()],
      ),
    });

    this.formArrayItems = this.formFiltersEditor.get(
      'items',
    ) as UntypedFormArray;
  }

  private handleQuickFiltersCountsChanges() {
    this.notDefaultDNDPlaceholderIsHidden =
      this.quickFilterItems.length == 1 ||
      (this.quickFilterItems.length == 2 && this.quickFilterItems[0].default);
  }

  private initDraggable() {
    this.draggable = {
      // note that data is handled with JSON.stringify/JSON.parse
      // only set simple data or POJO's as methods will be lost
      data: this.quickFilterItems,
      effectAllowed: 'all',
      disable: false,
      handle: false,
    };
  }

  doSave() {
    const names = (this.formFiltersEditor.controls.items as UntypedFormArray)
      .value;
    for (const [index, qf] of this.quickFilterItems.entries()) {
      qf.name = names[index];
    }

    this.filterService.saveQuickFilters(
      this.filtersName,
      this.quickFilterItems,
    );
    this.activeModal.close(this.quickFilterItems);
  }

  deleteFilterItem(item: QuickFilter, index: number) {
    this.quickFilterItems.splice(index, 1);

    if (item.name == this.newFilterName) {
      this.newFilterName = undefined;
    }

    const ar = this.formFiltersEditor.controls.items as UntypedFormArray;
    ar.removeAt(index);

    this.handleQuickFiltersCountsChanges();
  }

  close() {
    this.activeModal.dismiss();
  }

  onDrop(event: DndDropEvent) {
    const oldIndex = this.quickFilterItems.findIndex((item) => {
      return (event.data as QuickFilter).name == item.name;
    });

    let newIndex = this.quickFilterItems.length - 1;
    if (event.index && event.index < this.quickFilterItems.length) {
      newIndex = event.index;
    }

    this.quickFilterItems[oldIndex].default = false;

    this.moveQuickFilter(oldIndex, newIndex);
    this.handleQuickFiltersCountsChanges();
  }

  onDropNewDefaultQuickFilter(event: DndDropEvent) {
    const { name } = event.data as QuickFilter;
    this.setDefaultQuickFilter(name);
    this.handleQuickFiltersCountsChanges();
  }

  private setDefaultQuickFilter(name: string) {
    const oldIndex = this.quickFilterItems.findIndex((item) => {
      return item.name == name;
    });

    if (this.quickFilterItems.length > 0) {
      for (const x of this.quickFilterItems) {
        x.default = false;
      }
    }
    this.quickFilterItems[oldIndex].default = true;

    if (oldIndex != 0) {
      this.moveQuickFilter(oldIndex, 0);
    }
  }

  private moveQuickFilter(oldIndex: number, newIndex: number) {
    this.arrayMove(this.quickFilterItems, oldIndex, newIndex);

    const ar = this.formFiltersEditor.controls.items as UntypedFormArray;
    const c = ar.at(oldIndex);

    ar.removeAt(oldIndex);
    ar.insert(newIndex, c);
  }

  private arrayMove(
    arr: (QuickFilter | undefined)[],
    oldIndex: number,
    newIndex: number,
  ) {
    while (oldIndex < 0) {
      oldIndex += arr.length;
    }

    while (newIndex < 0) {
      newIndex += arr.length;
    }

    if (newIndex >= arr.length) {
      let k = newIndex - arr.length + 1;
      while (k--) {
        arr.push(undefined);
      }
    }

    arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
  }

  addNewFilter() {
    const modal = this.modalService.open(AddQuickFiltersModalComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      size: 'sm',
    });

    const modalInstance =
      modal.componentInstance as AddQuickFiltersModalComponent;
    modalInstance.quickFiltersNames = this.quickFilterItems.map((x) => {
      return x.name;
    });

    modal.result
      .then((result) => {
        if (result) {
          const modalResult = result as AddQuickFiltersModalResult;
          this.quickFilterItems.push({
            default: modalResult.setAsDefault,
            name: modalResult.filterName,
            values: this.currentFiltersFormValues,
          });

          this.initDraggable();

          const itemsFormArray = this.formFiltersEditor.get(
            'items',
          ) as UntypedFormArray;
          itemsFormArray.push(this.fb.control(modalResult.filterName));

          this.newFilterName = modalResult.filterName;

          if (modalResult.setAsDefault) {
            this.setDefaultQuickFilter(this.newFilterName);
          }

          this.handleQuickFiltersCountsChanges();
        }
      })
      .catch((error: unknown) => {
        if (error) {
          this.alertService.error(error as HttpErrorResponse | string);
        }
      });
  }

  getValidationMessage(i: number) {
    const control = this.formArrayItems.at(i);
    const isControlValueNotUnique = this.isControlValueNotUnique(i);

    if (control.invalid || isControlValueNotUnique) {
      if (control.errors?.required) {
        return 'This field is required';
      } else if (control.errors?.minlength) {
        return `Min length: ${control.errors.minlength.requiredLength}`;
      } else if (control.errors?.maxlength) {
        return `Max length: ${control.errors.maxlength.requiredLength}`;
      } else if (isControlValueNotUnique) {
        return 'Quick Filter name should be unique';
      }
    }

    return '';
  }

  isControlValueNotUnique(controlIndex: number): boolean {
    const control = this.formArrayItems.at(controlIndex);
    const value = control.value || null;

    if (!value) {
      return false;
    }

    return this.notUniqueControlsValues.some((x) => {
      return x == value;
    });
  }

  private uniqueNamesValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const formArray = control as UntypedFormArray;
      const names = formArray.value as string[];

      this.notUniqueControlsValues = [];

      for (const name of names) {
        const isNotUnique =
          names.filter((x) => {
            return x == name;
          }).length > 1;
        if (isNotUnique && !this.notUniqueControlsValues.includes(name)) {
          this.notUniqueControlsValues.push(name);
        }
      }

      return this.notUniqueControlsValues.length === 0
        ? null
        : { uniqueValues: this.notUniqueControlsValues };
    };
  }
}
type DraggableOptions = {
  // note that data is handled with JSON.stringify/JSON.parse
  // only set simple data or POJO's as methods will be lost
  data: QuickFilter[];
  disable: boolean;
  effectAllowed: EffectAllowed;
  handle: boolean;
};
